import * as amplitude from '@amplitude/analytics-browser';
import type { FeatureAttribute } from '@magicschool/business-logic/feature-flags';
import { DEFAULT_LOCALE, type Enums, type Language, type Tables } from '@magicschool/supabase/types';
import type { MagicSchoolUser, SessionClaimsUser, SupabaseUser } from '@magicschool/supabase/user';
import { isLocal } from '@magicschool/utils/nextjs/metadata';
import type { AuthError, AuthOtpResponse, OAuthResponse, Provider, UserResponse } from '@supabase/supabase-js';
import { edlinkLaunchUrl } from 'features/edlink/constants';
import type { InitialStateProps } from 'features/store/store';
import { createStoreSlice, type SetField } from 'features/store/zustand';
import { ERROR_STUDENT_ACCOUNT_ALREADY_EXISTS, ERROR_UNAUTHORIZED_DOMAIN } from '@/app/(public)/(auth)/auth/authFormStore';
import {
  analyticsHandleUserLogout,
  analyticsTrack,
  setAmplitudeUserId,
  updateAnalyticsUserProperties,
} from '@/util/amplitude/analyticsClient';
import { clientSupabase } from '@/util/supabase';
import { checkForExistingRoleUser, checkForUnauthorizedDomain, signInWithPassword } from './actions';
import { ERROR_CAPTCHA_FAILED, isCaptchaFailedAuthError } from './constants';
import { isExistingStudentAccountAuthError } from './existingStudentAccount';
import { handleSignOut } from './helpers';
import { requestPasswordReset } from './passwordResetConfirmation';

export type AuthStore = {
  session?: SessionClaimsUser | null;
  userData: MagicSchoolUser;
  userAttributes?: Tables<'user_attributes'>[];
  orgAttributes?: FeatureAttribute[];
  userToolAccessOverrides?: Tables<'user_tool_access_overrides'>[];
  orgToolAccessOverrides?: Tables<'org_tool_access_overrides'>[];
  userTrials?: (Tables<'user_trials'> & { user_trial_reasons: { reason_code: string } })[];
  isLTI: boolean;
  lms?: string | null;
  setField: SetField<AuthStore>;
  setLms: (lms: string) => void;
  signup: (email: string, password: string, locale: Language, agreedToTerms: boolean, captchaToken?: string) => Promise<void>;
  signin: (email: string, password: string, locale?: Language, captchaToken?: string) => Promise<void>;
  signinWithProvider: (providerName: Enums<'auth_provider'>, next: string, locale: Language) => Promise<OAuthResponse>;
  signinWithMagicLink: (email: string, captchaToken?: string) => Promise<AuthOtpResponse>;
  signOut: (args?: { deleted: boolean }) => Promise<void>;
  sendPasswordResetEmail: (email: string, captchaToken?: string) => Promise<void>;
  updatePassword: (new_password: string) => Promise<AuthError | undefined>;
  updateOrgAttribute: (orgAttribute: Tables<'org_attributes'>) => void;
  updateUserAttribute: (userAttribute: Tables<'user_attributes'>) => void;
  updateUserEmail: (email: string | null | undefined) => Promise<UserResponse | undefined>;
  updateUserTrials: (userTrials: (Tables<'user_trials'> & { user_trial_reasons: { reason_code: string } })[]) => void;
};

export const emptyUser = (): MagicSchoolUser => ({
  authProvider: '',
  best_practices_completed: false,
  city: null,
  confirmed_at: '',
  country: null,
  created_at: null,
  deleted_reason: null,
  district: null,
  edlink_id: null,
  email_confirmed_at: '',
  email: null,
  id: '',
  last_sign_in_at: '',
  locale: DEFAULT_LOCALE,
  name: null,
  org_id: null,
  plan: 1,
  profile_image: null,
  profile_status: 'new',
  providers: [],
  role: null,
  school: null,
  serial_id: 0,
  state: null,
  status: 'active',
  student_friendly_name: null,
  subscriptionPlanName: 'FREE',
  updated_at: '',
  user_role: null,
  integrations_user_id: null,
  is_demo: false,
  institutionRoles: [],
});

type InitialData = Pick<
  InitialStateProps,
  | 'userData'
  | 'session'
  | 'orgAttributes'
  | 'userAttributes'
  | 'userToolAccessOverrides'
  | 'orgToolAccessOverrides'
  | 'userTrials'
  | 'isLTI'
  | 'lms'
>;

export const createAuthStoreSlice = ({
  session,
  userData,
  userAttributes = [],
  orgAttributes = [],
  userToolAccessOverrides = [],
  orgToolAccessOverrides = [],
  userTrials = [],
  isLTI = false,
  lms = null,
}: InitialData) => {
  // Try to get the LMS value from localStorage if it's not provided in the initialData
  let lmsValue = lms;
  if (typeof window !== 'undefined' && !lmsValue) {
    const storedLms = localStorage.getItem('lms');
    if (storedLms) {
      lmsValue = storedLms;
    }
  }

  const trackSignInUpAnalytics = (authUser: SupabaseUser, eventName: string) => {
    setAmplitudeUserId(authUser.id);
    analyticsTrack(eventName, { type: 'Success', auth_provider: 'email' });
  };

  return createStoreSlice(
    'AuthStoreData',
    {
      session,
      userData: userData ?? emptyUser(),
      orgAttributes,
      userAttributes,
      userToolAccessOverrides,
      orgToolAccessOverrides,
      userTrials,
      isLTI,
      lms: lmsValue,
    },
    ({ get, set, setField }) => ({
      setField,
      setLms: (lmsValue: string) => {
        // Store in localStorage for improved persistence across the LTI experience
        if (typeof window !== 'undefined') {
          localStorage.setItem('lms', lmsValue);
        }
        set({ lms: lmsValue });
      },
      signin: async (email, password, locale, captchaToken) => {
        const result = await signInWithPassword(email, password, locale, captchaToken);

        if ('error' in result && result.error) {
          throw new Error(result.error.message);
        }

        if ('user' in result && result.user) {
          trackSignInUpAnalytics(result.user, 'SignIn:Success');
        }
      },
      signup: async (email, password, locale, agreedToTerms, captchaToken) => {
        const unauthorizedDomain = async () => checkForUnauthorizedDomain(email);
        // we need to check if the email exists in public.users because we have paths that don't create users in auth.users
        // as a result, the following will actually succeed and create a new account and confuse... everything.
        // if an account exists in public.users with a student role, error back and tell them to use SSO
        const studentAccountExists = async () => await checkForExistingRoleUser(email, 'student');
        if (await unauthorizedDomain()) {
          throw Error(ERROR_UNAUTHORIZED_DOMAIN);
        } else if (await studentAccountExists()) {
          throw Error(ERROR_STUDENT_ACCOUNT_ALREADY_EXISTS);
        } else if (!agreedToTerms) {
          throw new Error('You must agree to the terms of service to sign up'); //TODO ILANA
        } else {
          const url = new URL(`/api/auth/${isLocal() ? 'confirm' : 'callback'}`, window.location.origin);
          url.searchParams.set('locale', locale);
          const timeAgreedToTerms = new Date().toISOString();
          url.searchParams.set('timeAgreedToTerms', timeAgreedToTerms);

          const result = await clientSupabase.auth.signUp({
            email,
            password,
            options: {
              emailRedirectTo: url.toString(),
              captchaToken,
            },
          });
          if (isExistingStudentAccountAuthError(result.error)) {
            throw Error(ERROR_STUDENT_ACCOUNT_ALREADY_EXISTS);
          }
          if (isCaptchaFailedAuthError(result.error)) {
            throw Error(ERROR_CAPTCHA_FAILED);
          }
          if (result.error) {
            throw result.error;
          }
          if (result.data?.user) {
            trackSignInUpAnalytics(result.data.user, 'SignUp:Success');
          }
        }
      },
      signinWithProvider: async (providerName, next, locale) => {
        let options: { scopes: string; redirectTo: string } | { redirectTo: string };
        if (providerName === 'edlink') {
          window.location.href = edlinkLaunchUrl();
          return new Promise(() => null);
        }

        const url = new URL('/api/auth/confirm', window.location.origin);
        url.searchParams.set('locale', locale);
        if (next) {
          url.searchParams.set('next', next);
        }

        const sessionId = amplitude.getSessionId();
        const deviceId = amplitude.getDeviceId();
        if (sessionId) {
          url.searchParams.set('sessionId', sessionId.toString());
        }
        if (deviceId) {
          url.searchParams.set('deviceId', deviceId);
        }

        if (providerName === 'azure') {
          options = {
            scopes: 'email',
            redirectTo: url.toString(),
          };
        } else {
          options = {
            redirectTo: url.toString(),
          };
        }

        const result = await clientSupabase.auth.signInWithOAuth({
          provider: providerName as Provider,
          options: options,
        });

        // Currently no way to tell the difference between a new and returning user here
        analyticsTrack('SignUp:External', { type: providerName });

        return result;
      },
      signinWithMagicLink: async (email: string, captchaToken?: string) => {
        return await clientSupabase.auth.signInWithOtp({
          email,
          options: {
            emailRedirectTo: `${window.location.origin}/`,
            captchaToken,
          },
        });
      },
      signOut: async (args) => {
        const deleted = args?.deleted ?? false;
        set({ session: undefined });
        await handleSignOut({ deleted });
        analyticsHandleUserLogout();
      },
      sendPasswordResetEmail: async (email: string, captchaToken?: string) => {
        const url = new URL('/auth/changepass', window.location.origin);
        const result = await requestPasswordReset(email, url.toString(), captchaToken);
        if (result === 'captcha_failed') throw Error(ERROR_CAPTCHA_FAILED);
      },
      updatePassword: async (new_password: string) => {
        return clientSupabase.auth
          .updateUser({ password: new_password })
          .then(({ error }) => {
            if (error) {
              return error;
            }
          })
          .catch((error) => {
            return error;
          });
      },
      // Appends when absent: a default-on org feature has no row until an admin first turns it off.
      updateOrgAttribute: (orgAttribute) => {
        const currentOrgAttributes = get().orgAttributes;
        if (!currentOrgAttributes) {
          return;
        }
        const existing = currentOrgAttributes.some((attr) => attr.name === orgAttribute.name);
        set({
          orgAttributes: existing
            ? currentOrgAttributes.map((attr) => (attr.name === orgAttribute.name ? { ...attr, ...orgAttribute } : attr))
            : [...currentOrgAttributes, orgAttribute],
        });
      },
      // Appends when absent because a user saving a preference for the first time has no row yet.
      updateUserAttribute: (userAttribute) => {
        const current = get().userAttributes ?? [];
        const existing = current.some((attr) => attr.name === userAttribute.name);
        set({
          userAttributes: existing
            ? current.map((attr) => (attr.name === userAttribute.name ? { ...attr, ...userAttribute } : attr))
            : [...current, userAttribute],
        });
      },
      updateUserEmail: async (email) => {
        if (email && email !== get().userData.email) {
          const res = await clientSupabase.auth.updateUser({ email });
          if (res.data) {
            updateAnalyticsUserProperties({ email });
          }
          return res;
        }
      },
      updateUserTrials: (userTrials) => {
        set({ userTrials });
      },
    }),
  );
};
